import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDraft, saveDraft } from '../data/drafts';
import { getTemplateForm } from '../data/wizard';
import { hasReference, parsePickedRecord, resolveReferences } from '../data/referenceDefaults';
import { customValueLengthError, parseCustomValueConfig, parseDropdownConfig } from '../data/customValueConfig';
import { isVisible, parseVisibilityCondition } from '../data/visibility';
import { isOpenCollectionImage } from '../data/imageCollectionField';
import { isServiceProvided } from '../data/serviceProvidedField';
import type { FormField, TemplateForm } from '../data/types';

export type WizardStep = 'details' | 'slides' | 'outline' | 'review';

/** Every step name the router recognizes - not every template shows all of them, see wizardSteps. */
const ALL_WIZARD_STEPS: WizardStep[] = ['details', 'slides', 'outline', 'review'];

/** LB-06. Long enough not to chatter, short enough to matter on an 8–12 field form. */
const AUTO_SAVE_MS = 2 * 60 * 1000;

export function isWizardStep(value: string | undefined): value is WizardStep {
  return ALL_WIZARD_STEPS.includes(value as WizardStep);
}

/** A 'slides'/'outline' step only exists when the template has a matching property to fill. */
export function wizardSteps(form: TemplateForm | null): WizardStep[] {
  const fields = form?.fields ?? [];
  const hasSlides = fields.some((field) => field.propertyType === 'SlideCollection');
  const hasOutline = fields.some((field) => field.propertyType === 'List');

  const steps: WizardStep[] = ['details'];
  if (hasSlides) steps.push('slides');
  if (hasOutline) steps.push('outline');
  steps.push('review');
  return steps;
}

export interface WizardState {
  form: TemplateForm | null;
  /** Computed per template - 'slides' is only present when there's a SlideCollection field. */
  steps: WizardStep[];
  values: Record<string, string>;
  loading: boolean;
  error: string | null;
  draftId: string | null;
  setValue: (token: string, value: string) => void;
  visibleFields: (step: WizardStep) => FormField[];
  /** A disclosure on Details rather than a step of their own (UX-IA 5.4). */
  advancedFields: () => FormField[];
  /** Scoped to a step (default: the whole form) so a later step's required field cannot block leaving an earlier one. */
  missingRequired: (uptoStep?: WizardStep) => FormField[];
  /** Manually-typed Dropdown values outside their configured length bounds. */
  invalidLength: (uptoStep?: WizardStep) => FormField[];
  persist: (step: WizardStep, name?: string) => Promise<void>;
  /** DC-13: how long this sitting has lasted, for the time-saved measurement. */
  elapsedSeconds: () => number;
}

export function useWizard(templateId: string | undefined, resumeDraftId?: string | null): WizardState {
  const [form, setForm] = useState<TemplateForm | null>(null);
  const [entered, setEntered] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, true>>({});
  const [error, setError] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(resumeDraftId ?? null);

  // Monotonic, so a system clock change mid-session cannot corrupt the measurement.
  // A resumed draft starts a fresh sitting: only this visit's effort is being timed.
  const startedAt = useRef<number | null>(null);

  // Derived rather than stored: setting it in the effect body would trigger a
  // cascading render, and "loaded" simply means the form matches the route.
  const loading = error === null && form?.templateId !== templateId;

  useEffect(() => {
    if (!templateId) return;

    let cancelled = false;

    const load = async () => {
      try {
        const loaded = await getTemplateForm(templateId);
        if (cancelled) return;

        // A new document always starts from the server-resolved defaults (DC-12).
        // Unfinished work is resumed explicitly from the Drafts page, never implicitly.
        // Exception: an open collection Image's `resolvedDefault` is the reference KEY, not
        // a pickable item id - pre-filling it would look like an unmade pick was already made.
        const defaults = Object.fromEntries(
          loaded.fields.map((f) => [f.tokenName, isOpenCollectionImage(f) ? '' : f.resolvedDefault])
        );
        const saved = resumeDraftId ? (await getDraft(resumeDraftId)).values : {};
        if (cancelled) return;

        setEntered({ ...defaults, ...saved });
        // A resumed draft carries no edit history, so its reference defaults recompute.
        setTouched({});
        setForm(loaded);
        setError(null);
        startedAt.current = performance.now();
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load the template.');
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [templateId, resumeDraftId]);

  // A `{{Property}}` default follows the field it references until the user edits
  // it. Derived, not stored: writing this back would fight the user's own typing.
  // Fields arrive in editor order and a default may only reference an earlier
  // property, so feeding each result forward resolves chains in one pass.
  const values = useMemo(() => {
    if (!form) return entered;

    const resolved = { ...entered };

    for (const field of form.fields) {
      if (touched[field.tokenName] || !hasReference(field.defaultExpression)) continue;
      resolved[field.tokenName] = resolveReferences(field.defaultExpression, resolved);
    }

    return resolved;
  }, [form, entered, touched]);

  const setValue = useCallback((token: string, value: string) => {
    setEntered((current) => ({ ...current, [token]: value }));
    setTouched((current) => (current[token] ? current : { ...current, [token]: true }));
  }, []);

  const persist = useCallback(
    async (step: WizardStep, name?: string) => {
      if (!templateId) return;

      const saved = await saveDraft({ draftId: draftId ?? undefined, templateId, name, step, values });
      setDraftId(saved.draftId);
    },
    [templateId, draftId, values]
  );

  // Auto-save reads the latest state through a ref, so the timer is not restarted
  // on every keystroke — otherwise it would never fire while someone is typing.
  const latest = useRef({ persist, ready: false });
  useEffect(() => {
    // Nothing typed yet means the draft would hold only defaults — noise on the Drafts page.
    latest.current = { persist, ready: !loading && form !== null && Object.keys(touched).length > 0 };
  });

  useEffect(() => {
    if (!templateId) return;

    const timer = setInterval(() => {
      if (latest.current.ready) void latest.current.persist('details');
    }, AUTO_SAVE_MS);

    return () => clearInterval(timer);
  }, [templateId]);

  const stillVisible = useCallback(
    (fields: FormField[]) =>
      fields.filter((field) => isVisible(parseVisibilityCondition(field.visibilityConditionJson), values)),
    [values]
  );

  const steps = useMemo(() => wizardSteps(form), [form]);

  const visibleFields = useCallback(
    (step: WizardStep) => {
      const fields = form?.fields ?? [];
      const isSlideCollection = (field: FormField) => field.propertyType === 'SlideCollection';
      const isList = (field: FormField) => field.propertyType === 'List';
      if (step === 'review') return stillVisible(fields);
      if (step === 'slides') return stillVisible(fields.filter(isSlideCollection));
      if (step === 'outline') return stillVisible(fields.filter(isList));
      return stillVisible(fields.filter((field) => !field.isAdvanced && !isSlideCollection(field) && !isList(field)));
    },
    [form, stillVisible]
  );

  const advancedFields = useCallback(
    () =>
      stillVisible(
        (form?.fields ?? []).filter(
          (field) => field.isAdvanced && field.propertyType !== 'SlideCollection' && field.propertyType !== 'List'
        )
      ),
    [form, stillVisible]
  );

  /** Everything a person could have filled in by the time they reach (and try to leave) `uptoStep`. */
  const fieldsUpTo = useCallback(
    (uptoStep: WizardStep): FormField[] => {
      if (uptoStep === 'review') return form?.fields ?? [];

      const upToIndex = steps.indexOf(uptoStep);
      let result = [...visibleFields('details'), ...advancedFields()];

      for (const optional of ['slides', 'outline'] as const) {
        const optionalIndex = steps.indexOf(optional);
        if (optionalIndex >= 0 && optionalIndex <= upToIndex) {
          result = [...result, ...visibleFields(optional)];
        }
      }

      return result;
    },
    [form, steps, visibleFields, advancedFields]
  );

  // Details and Review each stand in front of the whole form, so there is one answer.
  const missingRequired = useCallback(
    (uptoStep: WizardStep = 'review') =>
      stillVisible(fieldsUpTo(uptoStep)).filter(
        (field) => field.isRequired && !isServiceProvided(field) && !values[field.tokenName]?.trim()
      ),
    [fieldsUpTo, stillVisible, values]
  );

  const invalidLength = useCallback(
    (uptoStep: WizardStep = 'review') =>
      stillVisible(fieldsUpTo(uptoStep)).filter((field) => {
        const value = values[field.tokenName] ?? '';
        if (field.propertyType === 'Dropdown') {
          const config = parseDropdownConfig(field.configJson);
          return config.allowCustomValue && customValueLengthError(value, config) !== null;
        }
        if (field.propertyType === 'People' || field.propertyType === 'Company' || field.propertyType === 'Project' || field.propertyType === 'Contact') {
          const config = parseCustomValueConfig(field.configJson);
          // A picked record travels as JSON; only a hand-typed value is length-checked.
          if (!config.allowCustomValue || value.trim() === '' || parsePickedRecord(value) !== null) return false;
          return customValueLengthError(value, config) !== null;
        }
        return false;
      }),
    [fieldsUpTo, stillVisible, values]
  );

  const elapsedSeconds = useCallback(
    () => (startedAt.current === null ? 0 : Math.round((performance.now() - startedAt.current) / 1000)),
    []
  );

  return {
    form,
    steps,
    values,
    loading,
    error,
    draftId,
    setValue,
    visibleFields,
    advancedFields,
    missingRequired,
    invalidLength,
    persist,
    elapsedSeconds
  };
}
